import React, { Suspense, useCallback, useMemo } from 'react';
import c from 'classnames';

import { Delete, EyeClose, EyeOutline } from '@lyearn/icons';
import { Box, DeprecatedIconButton, Loader, Tooltip, Typography } from '@lyearn/ui';

import { AdvancedMenuItemComponentProps } from '../../../../types/menus';
import AddSubtitle from './components/AddSubtitle';
import ListItem from './components/ListItem';
import UploadSubtitle from './components/UploadSubtitle';
import { languagesMap } from './consts';
import { iconButton } from './styles';
import { SubtitleActionTypes } from './types';
import { useSubtitle } from './useSubtitleActions';

function Subtitle(props: AdvancedMenuItemComponentProps) {
  const { view, currentSelectionNode, pos } = props;
  const { state, onCaptionSubmit, dispatch, t, onToggleCaption, onDeleteCaption } = useSubtitle(
    view,
    currentSelectionNode,
    pos,
  );
  const {
    captions: captionsState,
    loadingCaption,
    disabedCaptions: disabedCaptionsState,
    localFile,
  } = state;

  const setLocalFile = useCallback(
    (file: File | null) => {
      dispatch({ type: SubtitleActionTypes.File, payload: { localFile: file } });
    },
    [dispatch],
  );

  return (
    <Suspense fallback={null}>
      <Box>
        <Typography variant="label-02">{t('Lygo:AdvancedMenu.video.Subtitles.title')}</Typography>
        <Typography className="mt-4 text-text-secondary" variant="body-short-03">
          {t('Lygo:AdvancedMenu.video.Subtitles.description')}
        </Typography>
        {languagesMap.map((language, key) => {
          if (language.value !== loadingCaption && !captionsState[language.value]) {
            return null;
          }

          const isCaptionDisabled = disabedCaptionsState.includes(language.value);
          return (
            <Box key={key} className="group mt-20 flex cursor-pointer justify-between">
              <ListItem
                className={c(
                  'flex items-center',
                  language.value === loadingCaption
                    ? 'text-text-placeholder'
                    : 'text-icon-placeholder',
                )}
                disabled={isCaptionDisabled}
                flag={language.flag}
                label={language.label}
                languageLabel={language.languageLabel}
              />
              {language.value === loadingCaption ? (
                <Loader size={16} />
              ) : (
                <Box className="opacity-0 group-hover:opacity-100">
                  <Box className="flex">
                    <Tooltip
                      title={
                        isCaptionDisabled
                          ? (t('Lygo:AdvancedMenu.video.Subtitles.unhideButtonTooltip') as string)
                          : (t('Lygo:AdvancedMenu.video.Subtitles.hideButtonTooltip') as string)
                      }>
                      <DeprecatedIconButton
                        appearance="secondary"
                        className={c(
                          'h-20 w-20 me-8 hover:bg-interactive-secondary-hover',
                          iconButton,
                        )}
                        variant="ghost"
                        onClick={() => onToggleCaption(language.value)}>
                        {isCaptionDisabled ? (
                          <EyeClose className="h-20 w-20 text-icon-placeholder hover:text-icon-primary" />
                        ) : (
                          <EyeOutline className="h-20 w-20 text-icon-placeholder hover:text-icon-primary" />
                        )}
                      </DeprecatedIconButton>
                    </Tooltip>
                    <Tooltip title={t('Lygo:AdvancedMenu.video.Subtitles.deleteButtonTooltip')!}>
                      <DeprecatedIconButton
                        appearance="secondary"
                        className={c('h-20 w-20 hover:bg-interactive-secondary-hover', iconButton)}
                        variant="ghost"
                        onClick={() => onDeleteCaption(language.value)}>
                        <Delete className="h-20 w-20 text-text-error" />
                      </DeprecatedIconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
        {localFile ? (
          <UploadSubtitle
            captionsState={captionsState}
            file={localFile}
            setFile={setLocalFile}
            onCaptionSubmit={onCaptionSubmit}
          />
        ) : null}
        <AddSubtitle file={localFile} loadingCaption={loadingCaption} setFile={setLocalFile} />
      </Box>
    </Suspense>
  );
}

export default Subtitle;
